import React, { useCallback, useEffect, useState } from 'react';
import { Search, X, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';
import { marketingService } from '../../../services/api';
import MaskedMobile from '../../common/MaskedMobile';
import DateRangePicker from '../../common/DateRangePicker';
import TableSkeleton from '../../common/TableSkeleton';
import Pagination from '../../common/Pagination';
import Select from '../../common/Select';
import { formatDateToDdmmyyyy } from '../../../utils/format';
import { personOption, staffFilterValue } from '../../common/StaffFilterOption';

interface LeadRow {
  lead_id: string;
  full_name: string | null;
  mobile_number: string | null;
  email: string | null;
  city: string | null;
  job_title: string | null;
  platform: string | null;
  lead_status: string | null;
  uploaded_date: string | null;
  assigned_at: string | null;
  updated_at: string | null;
  assigned_to_mobile: string | null;
  assigned_to_name: string | null;
  added_by_mobile: string | null;
  added_by_name: string | null;
}

interface TeamMember { mobile: string; name: string; role: string }

// The statuses the API counts. Kept in the API's own order so the tab strip
// reads the same way the counts object does.
const STATUSES = ['All', 'New', 'Hot Lead', 'Call Again', 'Interested',
  'Not Interested', 'Not Answered', 'Visit', 'Closed', 'Onboarded', 'Spam'];

const STATUS_TONE: Record<string, string> = {
  'New': 'bg-sky-50 text-sky-700',
  'Hot Lead': 'bg-rose-50 text-rose-700',
  'Call Again': 'bg-amber-50 text-amber-700',
  'Interested': 'bg-emerald-50 text-emerald-700',
  'Not Interested': 'bg-slate-100 text-slate-600',
  'Not Answered': 'bg-slate-100 text-slate-600',
  'Visit': 'bg-violet-50 text-violet-700',
  'Closed': 'bg-indigo-50 text-indigo-700',
  'Onboarded': 'bg-emerald-100 text-emerald-800',
  'Spam': 'bg-rose-100 text-rose-800',
};

const fmtDate = (v?: string | null) => {
  if (!v) return '—';
  const dt = new Date(v);
  if (isNaN(dt.getTime())) return v;
  return formatDateToDdmmyyyy(dt);
};

/**
 * Read-only lead overview under Marketing.
 *
 * Admin/SuperAdmin see every lead in the system with the full status breakdown;
 * a Marketing Manager/Lead sees their subtree and an Executive their own — the
 * scoping is the API's, not this component's. The employee picker comes from
 * the API's own `team` roster, which for an admin is every marketing user.
 */
const LeadsOverviewTab: React.FC = () => {
  const navigate = useNavigate();
  const { adminMobile } = useAppSelector((s: RootState) => s.auth);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState('All');
  const [employee, setEmployee] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateField, setDateField] = useState('uploaded_date');
  const [search, setSearch] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async () => {
    if (!adminMobile) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | boolean | undefined> = {
        date_filter_type: dateField,
        lead_status: status !== 'All' ? status : undefined,
        assigned_to: employee || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        search: search.trim() || undefined,
        page,
        page_size: pageSize,
      };
      const res = await marketingService.listLeadsOverview(adminMobile, params);
      if (res?.statuscode === 200) {
        const fetchedLeads: LeadRow[] = res.leads || [];
        setLeads(fetchedLeads);

        const rawCounts: Record<string, number> = res.counts || {};
        // Sum all status counts to compute total across all statuses for 'All'
        const sumAll = Object.entries(rawCounts).reduce((acc: number, [k, v]) => {
          if (k.toLowerCase() === 'all' || k.toLowerCase() === 'total') return acc;
          const num = typeof v === 'number' ? v : Number(v);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);

        const totalAllLeads = rawCounts['All'] ?? rawCounts['all'] ?? rawCounts['total'] ?? res?.total_count ?? res?.total ?? res?.total_leads ?? sumAll;

        // Ensure 'All' in counts reflects the total count for the status strip
        setCounts({ ...rawCounts, All: totalAllLeads });
        setTeam(res.team || []);

        let resolvedTotal = (
          res?.total_filtered ??
          res?.total_count ??
          res?.total ??
          res?.total_leads ??
          res?.count ??
          (status !== 'All' ? rawCounts[status] : totalAllLeads)
        );

        if (resolvedTotal === undefined || resolvedTotal === null || isNaN(Number(resolvedTotal)) || Number(resolvedTotal) === 0) {
          resolvedTotal = fetchedLeads.length;
        } else {
          resolvedTotal = Number(resolvedTotal);
        }

        // If backend returned more leads unpaginated in the array, ensure the total reflects at least that size
        if (fetchedLeads.length > resolvedTotal) {
          resolvedTotal = fetchedLeads.length;
        }

        // The backend already paginates and reports total_pages directly —
        // trust it over any client-side guess, which can otherwise conjure a
        // phantom empty page whenever a page happens to be exactly full.
        setTotalPages(
          typeof res?.total_pages === 'number' && res.total_pages > 0
            ? res.total_pages
            : Math.max(1, Math.ceil(resolvedTotal / pageSize))
        );
      } else {
        setError(res?.detail || res?.message || 'Failed to load leads');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [adminMobile, dateField, status, employee, fromDate, toDate, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const displayedLeads = leads.length > pageSize
    ? leads.slice((page - 1) * pageSize, page * pageSize)
    : leads;

  const inputCls = 'h-[42px] pl-3 pr-8 rounded-xl border border-slate-200 shadow-sm text-sm font-semibold text-slate-700 bg-white outline-none appearance-none focus:border-[#1B4332] transition-all duration-200';

  return (
    <div className="w-full space-y-6 pb-12 font-sans">
      {/* Filters — search + employee on the left, remaining filters grouped on the right */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-auto min-w-0 sm:min-w-[240px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
            <input
              type="text"
              placeholder="Name, mobile, email"
              className="h-[42px] pl-10 pr-8 w-full text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 outline-none focus:border-[#1B4332] transition-all duration-200"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            {search && (
              <button
                onClick={() => {
                  setSearch('');
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 font-bold bg-transparent border-none cursor-pointer"
                type="button"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <Select
            value={employee}
            onChange={(v) => {
              setEmployee(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'All employees' },
              ...team.map((m) => personOption(m, { detail: m.role, status: true })),
            ]}
            renderValue={staffFilterValue(team)}
            className={`${inputCls} w-[10rem] truncate`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:ml-auto">
          <Select
            value={dateField}
            onChange={(v) => {
              setDateField(v);
              setPage(1);
            }}
            options={[
              { value: 'uploaded_date', label: 'Uploaded date' },
              { value: 'assigned_date', label: 'Assigned date' },
              { value: 'updated_date', label: 'Updated date' },
            ]}
            className={inputCls}
          />

          <DateRangePicker
            startDate={fromDate}
            endDate={toDate}
            onChange={(start, end) => {
              setFromDate(start);
              setToDate(end);
              setPage(1);
            }}
            onClear={() => {
              setFromDate('');
              setToDate('');
              setPage(1);
            }}
            accentColor="#10b981"
          />

          {(employee || fromDate || toDate || search || status !== 'All') && (
            <button type="button"
              onClick={() => {
                setEmployee('');
                setFromDate('');
                setToDate('');
                setSearch('');
                setStatus('All');
                setPage(1);
              }}
              className="flex items-center justify-center h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold">
              Clear
            </button>
          )}

          <button
            type="button"
            onClick={load}
            disabled={loading}
            title="Refresh"
            className={`flex items-center justify-center gap-2 h-[42px] px-4 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-600 outline-none hover:bg-slate-50 hover:ring-2 hover:ring-emerald-500/20 transition-all duration-200 cursor-pointer font-semibold ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Status strip. Every status is always shown with its count, so a zero
          reads as "none in this status" rather than the tab being missing. */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((st) => (
          <button
            key={st}
            type="button"
            onClick={() => {
              setStatus(st);
              setPage(1);
            }}
            className={`px-3 py-1.5 rounded-xl text-[12px] font-bold transition-all ${
              status === st
                ? 'bg-[#1B4332] text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {st} <span className="opacity-70">({counts[st] ?? 0})</span>
          </button>
        ))}
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">S.No</th>
                {['Lead', 'Location & Occupation', 'Status', 'Assigned To', 'Uploaded By', 'Platform', 'Uploaded', 'Updated'].map((h) => (
                  <th key={h} className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${
                    h === 'Location & Occupation' || h === 'Status' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableSkeleton rows={10} cols={9} />
            </tbody>
          </table>
        </div>
      )}
      {!loading && error && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-rose-500 font-medium">
          {error}
        </div>
      )}
      {!loading && !error && leads.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-12 text-center text-slate-400 font-medium">
          No leads match these filters.
        </div>
      )}

      {!loading && !error && leads.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-100">
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">S.No</th>
                {['Lead', 'Location & Occupation', 'Status', 'Assigned To', 'Uploaded By', 'Platform', 'Uploaded', 'Updated'].map((h) => (
                  <th key={h} className={`px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap ${
                    h === 'Location & Occupation' || h === 'Status' ? 'text-center' : 'text-left'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayedLeads.map((l, idx) => (
                <tr key={l.lead_id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="px-4 py-4 text-center text-slate-400 font-semibold text-[12px]">{(page - 1) * pageSize + idx + 1}</td>
                  <td className="px-4 py-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/leads/${encodeURIComponent(l.lead_id)}`)}
                      className="block text-left bg-transparent border-none p-0 cursor-pointer font-bold text-slate-800 text-[13px] whitespace-nowrap hover:text-indigo-600 hover:underline transition-colors"
                      title="Open lead details"
                    >
                      {l.full_name || '—'}
                    </button>
                    <MaskedMobile mobile={l.mobile_number || ''} className="text-slate-500 font-medium text-[12px]" />
                  </td>
                  <td className="px-4 py-4 text-center text-[12px]">
                    <div className="text-slate-700 font-medium whitespace-nowrap">{l.city || '—'}</div>
                    <div className="text-slate-400 text-[11px]">{l.job_title || '—'}</div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`inline-flex px-2 py-1 rounded-lg text-[10px] font-bold uppercase whitespace-nowrap ${
                      STATUS_TONE[l.lead_status || ''] || 'bg-slate-100 text-slate-500'}`}>
                      {l.lead_status || 'New'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-[12px]">
                    {l.assigned_to_mobile
                      ? <>
                          <div className="font-semibold text-slate-700 whitespace-nowrap">{l.assigned_to_name || '—'}</div>
                          <MaskedMobile mobile={l.assigned_to_mobile} className="text-slate-500 text-[11px]" />
                        </>
                      : <span className="text-slate-300 italic">unassigned</span>}
                  </td>
                  <td className="px-4 py-4 text-[12px]">
                    <div className="font-semibold text-slate-700 whitespace-nowrap">{l.added_by_name || '—'}</div>
                    {l.added_by_mobile && <MaskedMobile mobile={l.added_by_mobile} className="text-slate-500 text-[11px]" />}
                  </td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[12px] whitespace-nowrap">{l.platform || '—'}</td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[12px] whitespace-nowrap">{fmtDate(l.uploaded_date)}</td>
                  <td className="px-4 py-4 text-slate-600 font-medium text-[12px] whitespace-nowrap">{fmtDate(l.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages || 1}
        onPageChange={(p) => {
          setPage(p);
          document.getElementById('dashboard-main-scroll-container')?.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
};

export default LeadsOverviewTab;
